namespace $.$$ {
	export class $shm_hitalama_map_touch extends $.$shm_hitalama_map_touch {

		private readonly touch_capture_handled = new WeakSet< PointerEvent >()
		private readonly touch_capture_ids = new Set< number >()
		private touch_capture_gesture = false
		private touch_tap_start: PointerEvent | null = null
		private readonly contextmenu_tap = new $shm_hitalama_map_tap
		private contextmenu_click: PointerEvent | null = null
		private selection_hold: $mol_after_timeout | null = null
		private selection_start: PointerEvent | null = null
		private selection_pointer: number | null = null

		@ $mol_mem
		selection_feedback( next?: { point: readonly number[], active: boolean, moving?: boolean } | null ) {
			return next === undefined ? null : next
		}

		selection_pane() { return this.Map_page().Pane() as $shm_hitalama_map_pane }

		selection_contextmenu_capture = ( event: Event )=> {
			if( this.selection_pointer === null ) return
			event.preventDefault()
			event.stopPropagation()
		}

		@ $mol_mem
		selection_contextmenu_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'contextmenu', this.selection_contextmenu_capture, true )
		}

		selection_hold_cancel() {
			this.selection_hold?.destructor()
			this.selection_hold = null
			this.selection_start = null
			this.selection_feedback( null )
		}

		selection_hold_start( event: PointerEvent ) {
			if( this.tool_active() || this.touch_target_widget( event ) ) return
			if( !event.composedPath().some( target => target instanceof Element && target.hasAttribute( 'shm_hitalama_map_back' ) ) ) return
			this.selection_start = event
			this.selection_feedback({ point: [ event.clientX, event.clientY ], active: false })
			this.selection_hold = new $mol_after_timeout( 600, ()=> this.selection_activate( event ) )
		}

		selection_activate( event: PointerEvent ) {
			if( this.selection_start !== event ) return
			this.selection_hold_cancel()
			if( this.touch_capture_ids.size !== 1 || this.tool_active() ) return
			this.contextmenu_tap.cancel( event )
			this.touch_tap_start = null
			this.selection_pointer = event.pointerId
			this.start_pos( null )
			this.action_type( '' )
			this.Map_page().contextmenu_showed( false )
			this.Map_page().deselect_all()
			this.selection_pane().select_touch_start( event )
			this.selection_feedback({ point: [ event.clientX, event.clientY ], active: true })
		}

		destructor() {
			this.selection_hold_cancel()
			if( this.selection_pointer !== null ) this.selection_pane().select_finish()
			super.destructor()
		}

		contextmenu_click_capture = ( event: MouseEvent )=> {
			const opening = this.contextmenu_click
			this.contextmenu_click = null
			if( !opening || event.detail === 0 ) return
			const pointer = event as PointerEvent
			if( pointer.pointerType ) {
				if( pointer.pointerType !== 'touch' || pointer.pointerId !== opening.pointerId ) return
			} else {
				if( event.timeStamp - opening.timeStamp > 500 ) return
				if( Math.hypot( event.clientX - opening.clientX, event.clientY - opening.clientY ) > 8 ) return
			}
			event.preventDefault()
			event.stopPropagation()
		}

		contextmenu_target( event: PointerEvent ) {
			if( this.tool_active() || !this.touch_target_inside( event ) ) return null
			for( const target of event.composedPath() ) {
				if( !( target instanceof Element ) ) continue
				if( target.matches( 'a, input, textarea, select, [contenteditable], [mol_button], [rise_drag_view], [rise_resize_edge], [shm_hitalama_map_page_dragger], [shm_hitalama_map_widget_float_toolbar]' ) ) return null
				if( target.hasAttribute( 'shm_hitalama_map_widget_float' ) || target.hasAttribute( 'shm_hitalama_map_back' ) ) return target
			}
			return null
		}

		private touch_pointer_events( next?: readonly PointerEvent[] ) {
			const touch = this as unknown as {
				pointer_events( next?: readonly PointerEvent[] ): readonly PointerEvent[]
			}
			return touch.pointer_events( next )
		}

		private touch_event_eat( event: PointerEvent ) {
			const touch = this as unknown as {
				event_eat( event: PointerEvent ): string
			}
			return touch.event_eat( event )
		}

		auto() {
			this.touch_pointer_events()
			this.start_pan()
			this.start_pos()
			this.start_distance()
			this.start_zoom()
			this.action_type()
			this.view_rect()
			return [
				this.touch_capture_start_listener(),
				this.touch_capture_move_listener(),
				this.touch_capture_end_listener(),
				this.touch_capture_cancel_listener(),
				this.contextmenu_click_listener(),
				this.selection_contextmenu_listener(),
			]
		}

		@ $mol_mem
		touch_capture_start_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'pointerdown', this.event_start_capture, true )
		}

		@ $mol_mem
		touch_capture_move_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'pointermove', this.event_move_capture, true )
		}

		@ $mol_mem
		touch_capture_end_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'pointerup', this.event_end_capture, true )
		}

		@ $mol_mem
		touch_capture_cancel_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'pointercancel', this.event_cancel_capture, true )
		}

		@ $mol_mem
		contextmenu_click_listener() {
			return new $mol_dom_listener( this.$.$mol_dom_context.document, 'click', this.contextmenu_click_capture, true )
		}

		touch_target_inside( event: PointerEvent ) {
			const pane = this.dom_node()
			return event.composedPath().includes( pane ) || pane.contains( event.target as Node | null )
		}

		touch_target_widget( event: PointerEvent ) {
			return event.composedPath().some( target =>
				target instanceof Element && (
					target.hasAttribute( 'shm_hitalama_map_widget_float' )
					// The floating drag handle is a sibling of the widgets in the pane.
					|| target.hasAttribute( 'shm_hitalama_map_page_dragger' )
				),
			)
		}

		event_start_capture = ( event: PointerEvent )=> {
			this.selection_hold_cancel()
			// A fresh press belongs to a deliberate menu action, even if the browser
			// omitted the compatibility click for the gesture that opened the menu.
			this.contextmenu_click = null
			if( event.pointerType !== 'touch' ) {
				this.contextmenu_tap.cancel( event )
				return
			}
			this.contextmenu_tap.start( event, this.contextmenu_target( event ) )
			if( !this.touch_target_inside( event ) && this.selection_pointer === null ) return
			if( !this.allow_pan() && !this.allow_zoom() ) return
			this.touch_capture_handled.add( event )
			this.touch_capture_ids.add( event.pointerId )

			if( this.touch_capture_ids.size >= 2 ) {
				if( this.selection_pointer !== null ) this.selection_pane().select_finish()
				this.selection_pointer = null
				this.selection_feedback( null )
				this.touch_tap_start = null
				const page = this.Map_page() as $shm_hitalama_map_page
				page.drag_cancel()
				this.tool_cancel()
				event.stopPropagation()
				this.touch_capture_gesture = true
				return super.event_start( event )
			}

			if( this.tool_active() || this.touch_target_widget( event ) ) {
				this.touch_event_eat( event )
				this.action_type( '' )
				return
			}

			this.touch_capture_gesture = true
			this.selection_hold_start( event )
			this.touch_tap_start = event.composedPath().some( target =>
				target instanceof Element && target.hasAttribute( 'shm_hitalama_map_back' ),
			) ? event : null
			event.stopPropagation()
			return super.event_start( event )
		}

		private touch_tap_moved( event: PointerEvent ) {
			const start = this.touch_tap_start
			return start && Math.hypot( event.clientX - start.clientX, event.clientY - start.clientY ) > 8
		}

		event_move_capture = ( event: PointerEvent )=> {
			if( event.pointerType === 'touch' ) this.contextmenu_tap.move( event )
			if( event.pointerType !== 'touch' || !this.touch_capture_ids.has( event.pointerId ) ) return
			this.touch_capture_handled.add( event )
			if( this.selection_pointer === event.pointerId ) {
				event.preventDefault()
				event.stopPropagation()
				const feedback = this.selection_feedback()
				if( feedback && !feedback.moving && Math.hypot( event.clientX - feedback.point[0], event.clientY - feedback.point[1] ) > 8 ) {
					this.selection_feedback({ ...feedback, moving: true })
				}
				this.selection_pane().select_touch_move( event )
				return
			}
			const start = this.selection_start
			if( start && start.pointerId === event.pointerId && Math.hypot( event.clientX - start.clientX, event.clientY - start.clientY ) > 8 ) this.selection_hold_cancel()
			if( this.touch_tap_moved( event ) ) this.touch_tap_start = null

			if( !this.touch_capture_gesture ) {
				this.touch_event_eat( event )
				this.action_type( '' )
				return
			}

			if( this.touch_capture_ids.size >= 2 ) event.stopPropagation()
			return super.event_move( event )
		}

		event_end_capture = ( event: PointerEvent )=> {
			if( this.selection_start?.pointerId === event.pointerId ) this.selection_hold_cancel()
			if( this.selection_pointer === event.pointerId ) {
				this.selection_pane().select_touch_move( event )
				this.selection_pane().select_finish()
				this.selection_pointer = null
				this.selection_feedback( null )
				this.contextmenu_click = event
				this.touch_capture_handled.add( event )
				this.touch_capture_ids.delete( event.pointerId )
				this.touch_pointer_events([])
				this.start_pos( null )
				this.touch_capture_gesture = false
				event.preventDefault()
				event.stopPropagation()
				return
			}
			const target = event.pointerType === 'touch' ? this.contextmenu_tap.end( event ) : null
			const result = this.touch_end_capture( event )
			if( target ) {
				// pointerup.preventDefault() does not suppress the compatibility click.
				// Its target can already be an item in the newly rendered menu.
				this.contextmenu_click = event
				event.preventDefault()
				event.stopPropagation()
				return $mol_wire_async( this.Map_page() as $shm_hitalama_map_page ).touch_contextmenu( target, event )
			}
			return result
		}

		private touch_end_capture( event: PointerEvent ) {
			if( event.pointerType !== 'touch' || !this.touch_capture_ids.has( event.pointerId ) ) return
			this.touch_capture_handled.add( event )
			if( this.touch_capture_ids.size >= 2 ) event.stopPropagation()
			this.touch_capture_ids.delete( event.pointerId )
			const tap = this.touch_tap_start
			const deselect = tap?.pointerId === event.pointerId && !this.touch_tap_moved( event )
			this.touch_tap_start = null

			if( !this.touch_capture_gesture ) {
				this.touch_pointer_events( this.touch_pointer_events().filter( pointer => pointer.pointerId !== event.pointerId ) )
				this.action_type( '' )
				return
			}

			const result = super.event_leave( event )
			if( !this.touch_capture_ids.size ) this.touch_capture_gesture = false
			if( deselect ) this.Map_page().deselect_all()
			return result
		}

		event_cancel_capture = ( event: PointerEvent )=> {
			if( this.selection_start?.pointerId === event.pointerId ) this.selection_hold_cancel()
			if( this.selection_pointer === event.pointerId ) {
				this.selection_pane().select_finish()
				this.selection_pointer = null
				this.selection_feedback( null )
			}
			if( event.pointerType === 'touch' ) this.contextmenu_tap.cancel( event )
			if( event.pointerType !== 'touch' || !this.touch_capture_ids.has( event.pointerId ) ) return
			this.touch_capture_handled.add( event )
			this.touch_capture_ids.delete( event.pointerId )
			this.touch_tap_start = null
			this.touch_pointer_events( this.touch_pointer_events().filter( pointer => pointer.pointerId !== event.pointerId ) )
			this.start_pos( null )
			this.action_type( '' )
			if( !this.touch_capture_ids.size ) this.touch_capture_gesture = false
			return $mol_wire_async( this.Map_page() as $shm_hitalama_map_page ).drag_cancel()
		}
		
		event_wheel( event: WheelEvent ) {
			if( !this.allow_wheel_zoom() ) return
			if( event.defaultPrevented ) return
			const result = super.event_wheel( event )
			// Wheel zoom changes the camera while the right-button gesture is
			// still held. Continue that gesture from the new camera and cursor.
			if( this.start_pos() && !this.touch_capture_gesture ) {
				this.start_pan( this.pan() )
				this.start_pos( this.pointer_center() )
			}
			return result
		}
		
		event_start( event: PointerEvent ) {
			if( this.touch_capture_handled.has( event ) ) return
			if( event.button != 2 ) return
			return super.event_start( event )
		}

		event_move( event: PointerEvent ) {
			if( this.touch_capture_handled.has( event ) ) return
			return super.event_move( event )
		}

		event_end( event: PointerEvent ) {
			if( this.touch_capture_handled.has( event ) ) return
			return super.event_end( event )
		}

		event_leave( event: PointerEvent ) {
			if( this.touch_capture_handled.has( event ) ) return
			if( event.pointerType === 'touch' && this.touch_capture_ids.has( event.pointerId ) ) {
				// Document listeners own this pointer until pointerup/pointercancel,
				// including movements over controls outside the map pane.
				return
			}
			return super.event_leave( event )
		}
		
	}
}
